import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react'
import axiosInstance from '../../services/axiosInstance'
import { useAuth } from '../../contexts/AuthContext'
import PageHeader from '../../components/common/PageHeader'
import './styles.scss'

export default function UserProfile() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  
  const [profileUser, setProfileUser] = useState(null)
  const [programs, setPrograms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [updatingRole, setUpdatingRole] = useState(false)

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true)
        
        // If it's a student viewing their own profile, use context data
        if (currentUser?.roles?.includes('USER') && currentUser.id?.toString() === id.toString()) {
          setProfileUser(currentUser)
          setLoading(false)
          return
        }

        const [userRes, progRes] = await Promise.all([
          axiosInstance.get(`/api/settings/users/${id}`),
          // Fetch programs to map program IDs to names
          // Fallback to empty if it fails (e.g. if the viewer is not an admin, though the endpoint might work)
          axiosInstance.get('/api/settings/programs').catch(() => ({ data: [] }))
        ])
        setProfileUser(userRes.data)
        setPrograms(progRes.data)
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load user profile.')
      } finally {
        setLoading(false)
      }
    }
    fetchProfileData()
  }, [id, currentUser])

  const handleRoleChange = async (e) => {
    const newRole = e.target.value
    setUpdatingRole(true)
    setError('')
    setSuccessMsg('')
    try {
      await axiosInstance.put(`/api/settings/users/${id}/role`, { role: newRole })
      
      // Update local state
      const newRoles = ['USER', newRole]
      setProfileUser({ ...profileUser, roles: newRoles })
      
      setSuccessMsg(`Successfully updated role to ${newRole}.`)
      setTimeout(() => setSuccessMsg(''), 4000)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user role.')
      setTimeout(() => setError(''), 4000)
    } finally {
      setUpdatingRole(false)
    }
  }

  if (loading) return <div className="user-profile-page"><div className="profile-loading">Loading profile...</div></div>
  if (!profileUser) return <div className="user-profile-page"><div className="alert alert-danger">{error}</div></div>

  const isSuperAdminViewer = currentUser?.roles?.includes('SUPER_ADMIN')
  const isAdminViewer = currentUser?.roles?.includes('ADMIN')
  const isTargetSuperAdmin = profileUser.roles?.includes('SUPER_ADMIN')

  // Super Admins can edit anyone except themselves (optional, but let's allow it or restrict to other users)
  // Admins can edit anyone who is NOT a Super Admin
  const canEditRole = isSuperAdminViewer || (isAdminViewer && !isTargetSuperAdmin)
  
  // Identify their primary role (excluding USER)
  const primaryRole = profileUser.roles?.filter(r => r !== 'USER')[0] || 'STAFF'

  // Map managed program IDs to names
  const assignedPrograms = profileUser.managedProgramIds
    ? profileUser.managedProgramIds.map(pid => {
        const p = programs.find(prog => prog.id === pid)
        return p ? p.name : 'Unknown Program'
      })
    : []

  return (
    <div className="user-profile-page">
      <PageHeader 
        title="User Profile"
        subtitle="View and manage user role and program access."
      >
        <button className="btn btn-outline" onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ArrowLeft size={16} /> Back
        </button>
      </PageHeader>

      {error && <div className="alert alert-danger mb-4"><AlertCircle size={18}/> {error}</div>}
      {successMsg && <div className="alert alert-success mb-4"><CheckCircle2 size={18}/> {successMsg}</div>}

      <div className="profile-content">
        <div className="profile-grid">
          
          {/* Left Column: Identity */}
          <div className="profile-card identity-card">
            <div className="profile-header">
              <div className="avatar-large">
                {(profileUser.fullName || profileUser.fullname) ? (profileUser.fullName || profileUser.fullname).charAt(0).toUpperCase() : '?'}
              </div>
              <div className="profile-title">
                <h2>{profileUser.fullName || profileUser.fullname}</h2>
                <p>{profileUser.email}</p>
              </div>
            </div>
            <div className="profile-body">
              <div className="info-item">
                <label>Current Status</label>
                <div className="value">
                  {(profileUser.isActive || profileUser.active !== false && profileUser.active) ? 
                    <span className="badge badge--success">Active</span> : 
                    <span className="badge badge--danger">Inactive</span>
                  }
                </div>
              </div>
              <div className="info-item mt-3">
                <label>System Role</label>
                <div className="value">
                  {profileUser.roles?.map(r => (
                    <span key={r} className={`badge ${r === 'USER' ? 'badge--neutral' : 'badge--primary'}`} style={{ marginRight: '4px' }}>
                      {r === 'USER' ? 'STUDENT' : r}
                    </span>
                  ))}
                  {(!profileUser.roles || profileUser.roles.length === 0) && (
                    <span className="badge badge--neutral">STUDENT</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Management */}
          <div className="management-column">
            {canEditRole && (
              <div className="profile-card">
                <div className="card-header-simple">
                  <h3>Role Management</h3>
                  <p className="text-muted">Select a new role for this user to instantly update their access.</p>
                </div>
                <div className="profile-body">
                  <div className="role-editor">
                    <select 
                      className="form-control" 
                      value={primaryRole}
                      onChange={handleRoleChange}
                      disabled={updatingRole || (isTargetSuperAdmin && !isSuperAdminViewer)}
                    >
                      {isSuperAdminViewer && <option value="SUPER_ADMIN">SUPER_ADMIN</option>}
                      <option value="ADMIN">ADMIN</option>
                      <option value="STAFF">STAFF</option>
                      <option value="AGENT">AGENT</option>
                    </select>
                    {updatingRole && <span style={{ marginLeft: '12px', fontSize: '0.85rem', color: '#64748b' }}>Updating...</span>}
                  </div>
                </div>
              </div>
            )}

            {assignedPrograms.length > 0 && (
              <div className="profile-card">
                <div className="card-header-simple">
                  <h3>Assigned Programs</h3>
                  <p className="text-muted">Programs this user is authorized to manage.</p>
                </div>
                <div className="profile-body">
                  <div className="program-list">
                    {assignedPrograms.map((pName, idx) => (
                      <span key={idx} className="program-tag">{pName}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
        </div>
      </div>
    </div>
  )
}
